import { Alert, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AppointmentType } from '../../../constants'
import { useAppointmentLocationState, useRescheduleContext, useSearchParameters } from '../../../hooks'
import { parseAppointmentType } from '../../../hooks/useScheduleFormDefaultValues'
import { AppointmentTypeSubstitution, getSubstituteAppointmentType } from '../../../hooks/useTrialGate/trialGates'
import { appointmentTypeToDisplayText } from '../../../util'

type AppointmentTypeSubstitutedAlertProps = {
  blockedAppointmentTypes: AppointmentType[]
  marginBottom?: number
  selectedAppointmentType: AppointmentType
  /**
   * What the surface actually substituted, when it knows. Preferred over the derivation below,
   * which cannot tell a real substitution from the user happening to pick the type one would have
   * produced - on the type-selection route the provider chooses the type themselves, and the
   * derived version claimed a substitution that never happened.
   */
  substitution?: AppointmentTypeSubstitution
}

/**
 * Tells the user that the appointment type they asked for was swapped for another one, because
 * their trial status blocks it (PX-1807, PX-3311, PX-3749). The swap itself happens silently, in
 * useScheduleFormDefaultValues for the surfaces built on that hook and in schedule/New/index.tsx
 * for the one that builds its own defaults.
 *
 * Prefers being told what was substituted over working it out. The derivation cannot tell a real
 * substitution from the user happening to pick the type one would have produced, which is exactly
 * what happens on the type-selection route, where it claimed a substitution that never occurred. It
 * survives only for the consultant surface, which does not yet report its own decision: exporting
 * that decision from useScheduleFormDefaultValues would let the derivation and the
 * `blockedAppointmentTypes` prop both go.
 *
 * Silent while rescheduling, where `appointment_type` is the existing appointment's own type and
 * nothing substitutes it.
 */
const AppointmentTypeSubstitutedAlert = ({
  blockedAppointmentTypes,
  marginBottom,
  selectedAppointmentType,
  substitution,
}: AppointmentTypeSubstitutedAlertProps) => {
  const { t } = useTranslation()
  const appointmentLocationState = useAppointmentLocationState()
  const rescheduleContext = useRescheduleContext()
  const searchParameters = useSearchParameters()

  if (rescheduleContext?.reason) return null

  // Only when the surface did not say: the derivation below reads context this component holds
  // anyway, but it allocates and re-parses on every render of a watch-driven form subtree, and a
  // surface that reports a substitution never needs it.
  const deriveSubstitution = (): AppointmentTypeSubstitution | undefined => {
    const requestedType = parseAppointmentType((appointmentLocationState?.appointment_type ?? searchParameters.appointment_type) as string)
    const substitutedType = getSubstituteAppointmentType(requestedType, blockedAppointmentTypes)

    return substitutedType ? { from: requestedType, to: substitutedType } : undefined
  }

  const appliedSubstitution = substitution ?? deriveSubstitution()

  // The single test both branches answer to: the note clears as soon as the user picks something
  // else by hand. Comparing against the type substituted *to* rather than the blocked one, which is
  // never selectable and so would leave this always true.
  if (appliedSubstitution?.to !== selectedAppointmentType) return null

  return (
    <Grid item xs={12} marginBottom={marginBottom}>
      <Alert severity="info">
        {t('pages.appointment.messages.appointmentTypeSubstituted', {
          requestedTypeName: appointmentTypeToDisplayText[appliedSubstitution.from],
          substitutedTypeName: appointmentTypeToDisplayText[appliedSubstitution.to],
        })}
      </Alert>
    </Grid>
  )
}

export { AppointmentTypeSubstitutedAlert }
